import type { PostgrestSingleResponse } from '@supabase/supabase-js';
import type { PostgrestResponseSuccess } from '@supabase/postgrest-js';
import {
  FunctionsFetchError,
  FunctionsHttpError,
  FunctionsRelayError,
  type FunctionsResponse,
} from '@supabase/functions-js';

import { supabase } from './supabase-client';
import type { Campaign, Merchant, ServerError } from './models/types';
import Bugsnag from '@bugsnag/js';

/**
 * Always re-throw an Error object because the UI expects an error object with message and stack properties, so that the snackbar and display the message correctly.
 * @param error
 */
const handleCatchError = (error: any) => {
  const errorCast = error as object;
  Bugsnag.notify(error);
  let errorMsg;

  // Error is ServerError
  if ('error' in errorCast && 'status' in errorCast) {
    const errorCast2 = errorCast as ServerError;
    errorMsg = errorCast2.error;
    throw new Error(errorMsg);
  }

  // Error is normal Error object
  if ('message' in errorCast && 'stack' in errorCast) {
    throw errorCast;
  }

  // Error is string
  if (typeof error === 'string') {
    throw new TypeError(error);
  }

  // Error is object with unknown properties
  if (error instanceof Object) {
    return new Error(JSON.stringify(error));
  }

  console.error('Unknown error');

  return new Error(error.toString());
};

export const handleError = async (response: Response) => {
  try {
    if (!response.ok) throw new Error(await response.json());
    return response;
  } catch (error) {
    throw handleCatchError(error);
  }
};

export const handleSupabaseDatabaseError = async (
  response: PostgrestSingleResponse<any>,
): Promise<PostgrestResponseSuccess<any>> => {
  try {
    if (response.error) {
      console.error(response.error.message);
      console.error(response.error.details);
      throw Error(response.error.details);
    }

    if (response.status >= 300) {
      console.error(`Status: ${response.status}`);
      console.error(response);
      throw new Error('Error fetching data.');
    }

    return response;
  } catch (error) {
    throw handleCatchError(error);
  }
};

export const handleSupabaseFunctionsError = async (
  response: FunctionsResponse<any>,
): Promise<FunctionsResponse<any>> => {
  try {
    if (response.error) {
      if (response.error instanceof FunctionsHttpError) {
        const errorObj = await (response.error.context as Response).json();
        throw errorObj;
      } else if (response.error instanceof FunctionsRelayError) {
        console.log('Relay error:', response.error.message);
        throw new Error(response.error.message);
      } else if (response.error instanceof FunctionsFetchError) {
        console.log('Fetch error:', response.error.message);
        throw new Error(response.error.message);
      } else if (
        response.error instanceof Error &&
        response.error.message.includes(
          `Failed to execute 'json' on 'Response': Unexpected end of JSON input`,
        )
      ) {
        // console.log(Object.prototype.toString.call(response.error));
        // Ignore this error, supabase edge functions always expect data from the function, but DELETE cannot return data except null
        return response;
      } else {
        console.error('Unknown error');
        console.error(response.error);
        throw new Error(response.error.message);
      }
    }
    return response;
  } catch (error) {
    throw handleCatchError(error);
  }
};

export const getMerchantCategories = async () => {
  const response = await supabase.from('merchant_categories').select('*');
  return await handleSupabaseDatabaseError(response);
};

export const getMerchantSubcategories = async () => {
  const response = await supabase.from('merchant_subcategories').select('*');
  return await handleSupabaseDatabaseError(response);
};

export const getCampaignImages = async (
  merchantId: number,
  campaignId: number,
) => {
  const response = await supabase
    .schema('app')
    .from('campaign_images')
    .select('*')
    .eq('merchant_id', merchantId)
    .eq('campaign_id', campaignId);
  return await handleSupabaseDatabaseError(response);
};

export const updateBrandPriorities = async (data: Merchant[]) => {
  const response = await supabase.functions.invoke('brand-priorities', {
    body: data,
    method: 'PATCH',
  });
  return await handleSupabaseFunctionsError(response);
};

export const updateCampaignPriorities = async (data: Campaign[]) => {
  const response = await supabase.functions.invoke('campaign-priorities', {
    body: data,
    method: 'PATCH',
  });
  return await handleSupabaseFunctionsError(response);
};

export const createMerchant = async (data: FormData) => {
  const response = await supabase.functions.invoke('merchants', {
    body: data,
    method: 'POST',
  });
  return await handleSupabaseFunctionsError(response);
};

export const updateMerchant = async (data: FormData) => {
  const response = await supabase.functions.invoke('merchants', {
    body: data,
    method: 'PATCH',
  });
  return await handleSupabaseFunctionsError(response);
};

export const getCampaigns = async () => {
  const response = await supabase
    .from('campaigns')
    .select('*, merchants(name)')
    .order('id', { ascending: true });

  return await handleSupabaseDatabaseError(response);
};

export const createCampaign = async (data: FormData) => {
  // const response = await supabase.functions.invoke('campaigns', {
  //   body: data,
  //   method: 'POST',
  // });
  // return await handleSupabaseFunctionsError(response);
  const response = await supabase.functions.invoke('campaigns', {
    body: data,
    method: 'POST',
  });
  return await handleSupabaseFunctionsError(response);
};

export const updateCampaign = async (data: FormData) => {
  const response = await supabase.functions.invoke('campaigns', {
    body: data,
    method: 'PATCH',
  });
  return await handleSupabaseFunctionsError(response);
};

export const deleteCampaign = async (
  merchantId: number,
  campaignId: number,
) => {
  const response = await supabase.functions.invoke(
    `campaigns/?merchant_id=${merchantId}&campaign_id=${campaignId}`,
    {
      method: 'DELETE',
    },
  );
  return await handleSupabaseFunctionsError(response);
};

export const getVoucher = async (voucherCode?: string) => {
  const route = `vouchers${voucherCode === undefined ? '' : `/${voucherCode}`}`;
  const response = await supabase.functions.invoke(route, {
    method: 'GET',
  });
  return await handleSupabaseFunctionsError(response);
};

export const redeemVoucher = async (voucherCode: string, remarks: string) => {
  const response = await supabase.functions.invoke(
    `vouchers/${voucherCode}/redeem`,
    {
      method: 'POST',
      body: { remarks },
    },
  );
  return await handleSupabaseFunctionsError(response);
};

export const processWithdrawalRequest = async (
  withdrawalRequestId: number,
  newStatus: 'completed' | 'rejected',
  remarks?: string,
) => {
  const response = await supabase.functions.invoke(
    `withdrawal-requests/${withdrawalRequestId}/process`,
    {
      method: 'POST',
      body: { new_status: newStatus, remarks },
    },
  );
  return await handleSupabaseFunctionsError(response);
};

/**
 * Brand (merchant) withdrawal requests.
 *
 * Reads come straight from the `admin_backend.brand_withdrawal_requests` table
 * (RLS scopes rows to the caller), so only these write helpers go through edge
 * functions.
 *
 * NOTE: both endpoints below are placeholders — the edge functions are to be
 * implemented later. Adjust the routes/bodies once the contract is finalised.
 */
export const createWithdrawalRequest = async (amount: number) => {
  const response = await supabase.functions.invoke('brand-withdrawal-requests', {
    method: 'POST',
    body: { amount },
  });
  return await handleSupabaseFunctionsError(response);
};

export const processBrandWithdrawalRequest = async (
  withdrawalRequestId: number,
  newStatus: 'completed' | 'rejected',
  remarks?: string,
) => {
  const response = await supabase.functions.invoke(
    `brand-withdrawal-requests/${withdrawalRequestId}/process`,
    {
      method: 'POST',
      body: { new_status: newStatus, remarks },
    },
  );
  return await handleSupabaseFunctionsError(response);
};

export const getInvoiceFile = async (invoiceId: string) => {
  const response = await fetch(
    `${
      import.meta.env.VITE_SUPABASE_URL
    }/functions/v1/invoices/${invoiceId}/file`,
    {
      headers: {
        Authorization: `Bearer ${
          (await supabase.auth.getSession()).data.session?.access_token
        }`,
      },
    },
  );
  return await handleError(response);
};

export const createBackendUser = async (data: FormData) => {
  const response = await supabase.functions.invoke('users', {
    body: data,
    method: 'POST',
  });
  return await handleSupabaseFunctionsError(response);
};

export const createBrandTransaction = async (data: FormData) => {
  const response = await supabase.functions.invoke('brand-transactions', {
    body: data,
    method: 'POST',
  });
  return await handleSupabaseFunctionsError(response);
};
